import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCampaignPageParam } from '../../useCampaignPageParam';
import {
  useCampaignPageSizeParam,
  PAGE_SIZE_OPTIONS,
} from '../../useCampaignPageSizeParam';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { apiClient } from '@discovr/common';
import { useCampaignPillParam } from '../../useCampaignPillParam';
import { Button } from '../../../components/ui/button';
import { SimpleTooltip } from '../../../components/ui/tooltip';
import { Plus, VideoCamera, VideoCameraSlash, MonitorPlay } from '@phosphor-icons/react';
import { canSubmitContentRow, fmtDateTime } from '../../utils/campaignDetailHelpers';
import {
  createCampaignDetailApi,
  useContentDetail,
  useMarkThreadRead,
  useMarkPostSeen,
  usePostRosterMessage,
  useEditRosterMessage,
  useDeleteRosterMessage,
  useRosterThread,
  useCampaignUiStore,
  useCampaignCapabilitiesStore,
  useCampaignEditorStore,
  useCampaignHeaderQuery,
  campaignKeys,
  useRosterCounts,
} from '@discovr/features';
import {
  resolvePlatform,
  type Platform,
} from '@discovr/types';
import type { RosterEntryContent } from '@discovr/types';
import { RosterTableShell } from '../parts/RosterTableShell';
import { RosterScreenHeader } from '../parts/RosterScreenHeader';
import { RosterSearchSort } from '../parts/RosterSearchSort';
import { resolvePipeline } from '../../utils/campaignPipeline';
import { ContentReviewPanel } from '../../../components/common/ContentReview/ContentReviewPanel';
import { UrlInputModal, type RequirePlatform } from '../UrlInputModal';
import { EmptyState } from '../../../components/common/EmptyState';
import { ReviewAction } from '../ReviewAction';
import { EtaTimeline } from '../EtaTimeline';
import { isThreadLockedError } from '../../../components/common/notificationErrors';
import { resolveThreadLocked } from '../../../components/common/resolveThreadLocked';
import { PlatformCreatorCell } from '../../../lib/platformProfile';
import { StatusBadge } from '../parts/StatusBadge';
import { JumpToTopPill } from '../parts/JumpToTopPill';
import { rowDisplayName } from '../../utils/rowDisplayName';
import { useCampaignReviewDeepLink } from '../../useCampaignReviewDeepLink';
import { useCampaignReviewControls } from '../../useCampaignReviewControls';
import { useCampaignBucketDeepLink } from '../../useCampaignBucketDeepLink';
import type { CampaignSearch } from '../../search';
import { ContentRowDetail } from './ContentRowDetail';
import {
  type ContentBucket,
  mapThreadMessages,
  contentStatusLabel,
  submitLabel,
} from './types';
import {
  DataTable,
  ColumnsMenu,
  columnVisibilityView,
} from '../../../components/common/DataTable';
import type { ColumnDef } from '../../../components/common/DataTable';
import {
  useRosterStatusTabs,
  useRosterTabSort,
  rosterSortStorageKey,
  useRosterColumnPreferences,
} from '../../hooks';
import type { RosterPage, RosterPillDef } from '../../hooks';
import { useRosterSearch } from '../../hooks/useRosterSearch';
import { usePublishSharePageInfo } from '../../hooks/usePublishSharePageInfo';
import { Skeleton } from '../../../components/ui/skeleton';
import { usePublicView } from '../../publicView';
import { usePublicPillCapture } from '../../usePublicPillCapture';
import { adaptPublicRosterRowsAsContentRows } from './publicRosterRowAdapter';

/** Stable no-op for pagination/pill callbacks in public read-only mode. */
const PUBLIC_NOOP = () => {};

// Singleton factory for the per-pill queries below.
const unifiedApi = createCampaignDetailApi();

/** "Fetch everything" sentinel — pagination happens client-side, not via this limit. */
const MAX_FETCH_LIMIT = 10000;


// Delay after a GEO Regenerate click before checking whether anything happened.
// The BE returns 202 whether it enqueued or silently no-op'd, so we wait one
// poll cycle to let a real 'pending'/'processing' transition show first.
const GEO_REGENERATE_NOOP_CHECK_MS = 6000;
const GEO_FINALIZE_POLL_MS = 5000;

const DRIVE_PLACEHOLDER = 'https://drive.google.com/file/d/…';

type UrlUploadPlatform = Platform;

export type UrlUploadCopy = {
  description: string;
  placeholder: string;
  requirePlatform: RequirePlatform;
};

const UPLOAD_COPY: Record<UrlUploadPlatform, UrlUploadCopy> = {
  youtube: {
    description:
      'Paste a YouTube URL or a Google Drive link. The brand will see this in the review panel.',
    placeholder: 'https://www.youtube.com/watch?v=… or https://drive.google.com/file/d/…',
    requirePlatform: 'youtube',
  },
  instagram: {
    description:
      'Paste a Google Drive link (drive.google.com/file/d/…). Make sure sharing is set to "Anyone with link".',
    placeholder: DRIVE_PLACEHOLDER,
    requirePlatform: 'instagram',
  },
};

/** Shared with ConfirmedTab.tsx's "Add Content" action so both tabs offer the
 *  same platform-aware copy/validation instead of a third duplicate. */
export function uploadCopy(platform: string | null | undefined): UrlUploadCopy {
  return UPLOAD_COPY[resolvePlatform(platform) === 'instagram' ? 'instagram' : 'youtube'];
}

const CONTENT_PILLS: RosterPillDef<ContentBucket>[] = [
  { key: 'all', label: 'All' },
  { key: 'review', label: 'Review', status: 'content_review' },
  { key: 'pending', label: 'Pending', status: 'content_pending' },
];

export function ContentTab() {
  const { campaignId } = useCampaignUiStore();
  const scriptStageOn = resolvePipeline(useCampaignUiStore((s) => s.campaignPipeline)).script_enabled;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Public (no-login) share view. campaignId stays null in public mode, so
  // every campaignId-gated query is disabled on its own; `enabled: !isPublicView`
  // on the list queries is explicit insurance on top of that.
  const { isPublicView, grant, rosterPage } = usePublicView();
  const {
    role,
    viewerIsSuperAdmin,
    permissions,
    handleAdminReviewContent,
    handleAdminSubmitContent,
    handleAdminSetEta,
    renderEmbeddedContentReviewAgent,
    aiContentReviewVersion,
  } = useCampaignCapabilitiesStore();
  const { data: campaignHeader } = useCampaignHeaderQuery(campaignId ?? '', { enabled: !isPublicView });
  const campaignPlatform = campaignHeader?.platform;
  // Pass campaignId so a sent message re-sorts the roster (recency) for the
  // sender, who is excluded from the new_message SSE.
  const postMessage = usePostRosterMessage(campaignId ?? undefined);
  const handleContentThreadComment = async (entryId: string, body: string, replyTo?: string) => {
    try {
      await postMessage.mutateAsync({ entryId, type: 'content', body, replyTo });
    } catch (err) {
      // BE returns 423 when the thread locked mid-session (the roster row's
      // locked flag is fetch-time). Flip the composer to locked immediately.
      if (isThreadLockedError(err)) {
        setThreadLockedLive(true);
        toast.error('This conversation is locked');
        if (campaignId) {
          void queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(campaignId) });
        }
      }
      // Re-throw so ChatThread rolls back the optimistic bubble — the message
      // never sent, so leaving it displayed would be a ghost message.
      throw err;
    }
  };
  const editMessage = useEditRosterMessage(campaignId ?? undefined);
  const deleteMessage = useDeleteRosterMessage(campaignId ?? undefined);
  const [contentActionBusy, setContentActionBusy] = useState(false);
  const [justApprovedIds, setJustApprovedIds] = useState<Set<string>>(new Set());

  const handleApproveContent = useCallback(
    async (entryId: string) => {
      setContentActionBusy(true);
      try {
        await handleAdminReviewContent?.(entryId, 'approve');
        setJustApprovedIds((prev) => new Set(prev).add(entryId));
        void queryClient.invalidateQueries({
          queryKey: campaignKeys.rosterTab(campaignId ?? '', 'content'),
        });
      } finally {
        setContentActionBusy(false);
      }
    },
    [handleAdminReviewContent, queryClient, campaignId],
  );
  const { setContentSubmitOpen, contentReviewFeedback, contentReviewUiMode } = useCampaignEditorStore();

  // Role-based default: admin sees Pending first, brand sees Review first,
  // each falling back if its bucket is empty.
  const isAdminViewer = !!permissions?.canSubmitContentOnBehalf;
  // The side the viewer's own messages align to. It must match the sender_role
  // the backend stamps on their message, so the optimistic bubble and the
  // persisted one land on the same side: admins post as 'admin', everyone else
  // as their brand role. (Previously hardcoded to 'brand', which pushed an
  // admin's own messages onto the receiver side.)
  const viewerActor = isAdminViewer ? 'admin' : (role ?? 'brand_owner');
  // Start null — the cascade decides the real tab once counts settle;
  // skeleton renders in the interim.
  const [activeTab, setActiveTab] = useState<ContentBucket | null>(null);
  const cascadeApplied = useRef(false);
  const [, setPillParam] = useCampaignPillParam('pending');

  // Sync active tab → URL pill param whenever it changes (covers both cascade
  // and user-click paths without relying on handleTabChange being called).
  useEffect(() => {
    if (activeTab !== null) {
      setPillParam(activeTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Reset tab + cascade + deep-link guards when the campaign changes, so the
  // new campaign starts fresh (also keeps StrictMode's dev double-invoke from
  // leaving the page stuck on the skeleton).
  useEffect(() => {
    cascadeApplied.current = false;
    deepLinkBucketApplied.current = '';
    deepLinkBucketAppliedKey.current = '';
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resets the tab to skeleton in lockstep with clearing the StrictMode one-shot cascade guards on a campaign switch; part of the deliberately ref-coupled deep-link state machine, not a simple sync.
    setActiveTab(null);

  }, [campaignId]);

  const handleTabChange = (id: ContentBucket) => {
    cascadeApplied.current = true;
    setActiveTab(id);
    setPillParam(id);
  };

  // ── Server-side pagination state ────────────────────────────────────────────
  const [page, setPage] = useCampaignPageParam();
  const [pageSize, setPageSize] = useCampaignPageSizeParam();
  // Bootstraps to 'all' while activeTab is null (pre-cascade). Public mode
  // pins the bucket to the grant's pill — only that one pill has real row data,
  // and pinning also bypasses the cascade (which never fires for a disabled query).
  const effectiveActiveTab: ContentBucket =
    isPublicView && grant?.pill ? (grant.pill as ContentBucket) : (activeTab ?? 'all');

  // Clicking a non-granted pill prompts sign-in with the click captured as a
  // resumable action; clicking the granted pill stays a no-op.
  const handlePublicPillClick = usePublicPillCapture('content', effectiveActiveTab);

  // Active bucket total from /roster/counts — drives the hybrid search decision.
  // Disabled in public mode: a public link never carries campaign-wide counts.
  const { data: countsForSearch } = useRosterCounts(campaignId ?? '', { enabled: !isPublicView });
  const activeContentTotal = !countsForSearch
    ? undefined
    : effectiveActiveTab === 'review'
      ? countsForSearch.phase.by_status.content_review
      : effectiveActiveTab === 'pending'
        ? countsForSearch.phase.by_status.content_pending
        : countsForSearch.phase.content;
  // Stable identity so the search-reset effect dep does not churn every render.
  const resetPageForSearch = useCallback(() => setPage(1), [setPage]);
  const searchState = useRosterSearch(activeContentTotal, pageSize, {
    onQueryChange: resetPageForSearch,
  });

  const getRowSearchText = useCallback((e: RosterEntryContent) => {
    return [
      (e.platform as { name?: string } | undefined)?.name ?? '',
      e.creator?.name ?? '',
      e.platform?.username ?? '',
    ].join(' ');
  }, []);

  // Server-side pagination: only the active pill fetches a page; badges come
  // from /roster/counts. While searching we fetch the whole bucket + local
  // filter (searching one page would silently hide matches).
  // Per-tab column sort (persisted), threaded into the query below. Creator is
  // the only sortable column here (matches the BE scriptContentSortFields).
  const rosterSort = useRosterTabSort<ContentBucket>(rosterSortStorageKey('content', campaignId), effectiveActiveTab);

  const buildPageQuery = useCallback(
    (pill: RosterPillDef<ContentBucket>, pageNum: number, size: number) => {
      const cid = campaignId ?? '';
      // One source for the sort params, spread into the query key AND the call so
      // the two never diverge — and so the search path sorts the bucket too (else
      // the header shows an active sort while the rows come back in default order).
      const sortParams = rosterSort.sortParamsFor(pill.key);
      // No server-side ?search= for content, so a searched bucket fetches once
      // and client-filters; small buckets take the normal paged path.
      if (searchState.serverSearch) {
        return {
          // Key by (pill, search, sort); `select` slices the visible page from the cache.
          queryKey: campaignKeys.rosterTab(cid, 'content', {
            status: pill.status,
            phase: `${pill.key}:bucket`,
            search: searchState.serverSearch,
            limit: MAX_FETCH_LIMIT,
            ...sortParams,
          }),
          queryFn: async () => {
            const res = await unifiedApi.listContent(cid, {
              status: pill.status,
              page: 1,
              limit: MAX_FETCH_LIMIT,
              ...sortParams,
            });
            const q = (searchState.serverSearch ?? '').toLowerCase();
            const rows = res.entries.filter((e) => getRowSearchText(e).toLowerCase().includes(q));
            return { entries: rows, total: rows.length, totalPages: 1 };
          },
          select: (bucket: RosterPage<RosterEntryContent>): RosterPage<RosterEntryContent> => {
            const start = (pageNum - 1) * size;
            return {
              entries: bucket.entries.slice(start, start + size),
              total: bucket.total,
              totalPages: Math.max(1, Math.ceil(bucket.total / size)),
            };
          },
          staleTime: 30_000,
          // Real, independently-honored gate (ANDed with !!campaignId, not clobbered).
          enabled: !isPublicView,
        };
      }
      return {
        queryKey: campaignKeys.rosterTab(cid, 'content', {
          status: pill.status,
          page: pageNum,
          limit: size,
          ...sortParams,
        }),
        enabled: !isPublicView,
        queryFn: () =>
          unifiedApi
            .listContent(cid, { status: pill.status, page: pageNum, limit: size, ...sortParams })
            .then((res) => ({ entries: res.entries, total: res.total, totalPages: res.totalPages })),
        staleTime: 30_000,
      };
    },
    [campaignId, searchState.serverSearch, getRowSearchText, isPublicView, rosterSort],
  );

  // Disabled in public mode: a public link never carries campaign-wide
  // counts, and inactive pills' badges must show a skeleton, not a false 0.
  const { data: rosterCounts } = useRosterCounts(campaignId ?? '', { enabled: !isPublicView });
  const contentCounts = useMemo<Partial<Record<ContentBucket, number>>>(
    () => ({
      all: rosterCounts?.phase.content ?? 0,
      review: rosterCounts?.phase.by_status.content_review ?? 0,
      pending: rosterCounts?.phase.by_status.content_pending ?? 0,
    }),
    [rosterCounts],
  );

  const {
    pills: contentPills,
    pageRows: filtered,
    activeRows,
    total,
    pageCount,
    isLoading: isInitialLoading,
    isError: isContentQueryError,
    isRefetching: isLoadingEntries,
  } = useRosterStatusTabs<RosterEntryContent, ContentBucket>({
    campaignId: campaignId || undefined,
    activeTab: effectiveActiveTab,
    pills: CONTENT_PILLS,
    buildPageQuery,
    counts: contentCounts,
    pageSize,
    page,
    setPage,
    clientFilter: searchState.clientQuery
      ? (r) => getRowSearchText(r).toLowerCase().includes(searchState.clientQuery)
      : undefined,
  });

  // Publishes pageSize/pageCount for the admin-header share button. Disabled in
  // public mode: that button doesn't exist there and pageCount would be wrong.
  usePublishSharePageInfo(pageSize, pageCount, !isPublicView);

  const pendingCount = contentPills.find((p) => p.key === 'pending')?.count ?? 0;
  const reviewCount = contentPills.find((p) => p.key === 'review')?.count ?? 0;
  const allCount = contentPills.find((p) => p.key === 'all')?.count ?? 0;

  // Public mode's actual data source: the granted pill's rows from the public
  // payload, adapted to the content-row shape (`filtered` above stays empty).
  const publicRows = useMemo(
    () => adaptPublicRosterRowsAsContentRows(rosterPage?.rows ?? []),
    [rosterPage],
  );
  const displayedRows = isPublicView ? publicRows : filtered;

  // Pill badges: every pill except the granted one shows a skeleton (a public
  // link carries no other pill's count). The granted pill shows its row count
  // only when it's a single page; multi-page falls back to 'loading'.
  const displayPills = useMemo(() => {
    if (!isPublicView) return contentPills;
    const grantedPillKey = grant?.pill;
    const grantedTotalPages = rosterPage?.total_pages ?? grant?.totalPages ?? 1;
    const grantedRowCount = rosterPage?.rows.length ?? 0;
    return contentPills.map((p) => ({
      ...p,
      count:
        p.key === grantedPillKey
          ? grantedTotalPages > 1
            ? ('loading' as const)
            : grantedRowCount
          : ('loading' as const),
    }));
  }, [contentPills, isPublicView, grant?.pill, grant?.totalPages, rosterPage]);

  const [dialogCreatorId, setDialogCreatorId] = useState<string | null>(null);
  // URL write-side companion to useCampaignReviewDeepLink: each open/close
  // mirrors the action to ?panel=content-review&creator=... so a refresh keeps
  // the panel open and a click produces a shareable link.
  const { openReview, closeReview, setThreadOpenUrl } = useCampaignReviewControls(
    'content-review',
    setDialogCreatorId,
  );

  // Locally flips the composer to locked on a 423 send failure. Reset per
  // opened entry so a stale lock doesn't leak across rows.
  const [threadLockedLive, setThreadLockedLive] = useState(false);
  useEffect(() => {
    setThreadLockedLive(false);
  }, [dialogCreatorId]);

  // Per-row Add Content URL modal state — null when closed; otherwise the
  // row's id + existing URL so the modal can pre-fill for re-submit.
  const [addContentRow, setAddContentRow] = useState<
    { id: string; existingUrl: string; name: string; expectedContentKind?: string } | null
  >(null);

  const dialogReviewVersion = dialogCreatorId
    ? (aiContentReviewVersion?.[dialogCreatorId] ?? 0)
    : 0;

  // Clear unread on dialog open. BE picks the latest message id when body is
  // empty, so we don't wait for the thread fetch. mutate is stable, so this
  // fires once per dialog open.
  const markThreadRead = useMarkThreadRead(campaignId ?? undefined);
  // Mark the current content version seen on dialog open too — clears the
  // floating "New" badge for this viewer (see ReviewAction's `seen` prop).
  const markPostSeen = useMarkPostSeen(campaignId ?? undefined);
  useEffect(() => {
    if (!dialogCreatorId) return;
    markThreadRead.mutate({ entryId: dialogCreatorId, type: 'content' });
    markPostSeen.mutate({ entryId: dialogCreatorId, phase: 'content' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogCreatorId]);

  // Content thread messages for the open dialog. The composer's send
  // invalidates this same key, so new messages render without a manual refetch.
  const contentThreadQuery = useRosterThread(dialogCreatorId ?? '', 'content');
  const contentThread = useMemo(
    () => mapThreadMessages(contentThreadQuery.data),
    [contentThreadQuery.data],
  );
  const handleContentThreadEdit = async (messageId: string, body: string) => {
    if (!dialogCreatorId) return;
    try {
      await editMessage.mutateAsync({ entryId: dialogCreatorId, type: 'content', messageId, body });
    } catch (err) {
      if (isThreadLockedError(err)) {
        setThreadLockedLive(true);
        toast.error('This conversation is locked');
      } else {
        toast.error('Could not edit the message');
      }
      throw err;
    }
  };
  const handleContentThreadDelete = async (messageId: string) => {
    if (!dialogCreatorId) return;
    try {
      await deleteMessage.mutateAsync({ entryId: dialogCreatorId, type: 'content', messageId });
    } catch (err) {
      if (isThreadLockedError(err)) {
        setThreadLockedLive(true);
        toast.error('This conversation is locked');
      } else {
        toast.error('Could not delete the message');
      }
      throw err;
    }
  };

  // Full content detail (URL + AI envelope) for the open dialog; refetches on
  // SSE bumps so the panel transitions pending → complete without a reopen.
  //
  // Poll-while-pending backstop: the SSE completion nudge is best-effort and
  // can be dropped, which would otherwise leave the GEO tab stuck on a spinner.
  // Uses useContentDetail's native `pollMs`. The separate state (not a derived
  // const) breaks an ordering cycle — pollMs is passed into the same call the
  // pending flag is derived from — so it's synced one render behind via an effect.
  const [geoFinalizePendingForPoll, setGeoFinalizePendingForPoll] = useState(false);
  const contentDetailQuery = useContentDetail(
    dialogCreatorId ?? '',
    dialogReviewVersion,
    geoFinalizePendingForPoll ? GEO_FINALIZE_POLL_MS : 0,
  );
  const contentDetail = contentDetailQuery.data ?? null;
  const contentDetailLoading = contentDetailQuery.isLoading;

  const geoFinalizeStatus = contentDetail?.geo_finalize?.status;
  const geoFinalizePending =
    !!dialogCreatorId && (geoFinalizeStatus === 'pending' || geoFinalizeStatus === 'processing');
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- deliberately one-render-behind: re-syncs the poll-driving flag off the query's own freshly-read data, since pollMs must be passed INTO the same useContentDetail call this data came FROM (see doc comment above) — a same-render read isn't possible, so this can't be expressed as a plain derived value.
    setGeoFinalizePendingForPoll(geoFinalizePending);
  }, [geoFinalizePending]);

  // Kept current every render so the delayed no-op check reads the latest
  // dialog/detail state, not whatever was current when the handler was defined.
  const dialogCreatorIdRef = useRef(dialogCreatorId);
  const geoFinalizeRef = useRef(contentDetail?.geo_finalize);
  useEffect(() => {
    dialogCreatorIdRef.current = dialogCreatorId;
    geoFinalizeRef.current = contentDetail?.geo_finalize;
  });

  // GEO finalize actions (admin-only). The finalize pack is generated async off
  // a BE webhook and refreshed via the SSE → query-invalidation pipeline (no
  // local polling). Each handler returns its request promise so GeoFinalizeTab
  // can await it for a busy state; errors are caught and logged, not re-thrown.
  //
  // Regenerate no-op guidance: the BE returns 202 whether it queued or silently
  // no-op'd (script GEO pack not ready, review incomplete, or a finalize already
  // running — indistinguishable from the response). As a best-effort heuristic,
  // if geo_finalize never moves to pending/processing and stays byte-for-byte
  // the same for GEO_REGENERATE_NOOP_CHECK_MS, assume nothing happened and nudge
  // the admin toward Script Review.
  const handleRegenerateGeoFinalize = async () => {
    if (!dialogCreatorId) return;
    const entryId = dialogCreatorId;
    const before = geoFinalizeRef.current;
    try {
      await apiClient.post(`/roster/${encodeURIComponent(entryId)}/geo/finalize/regenerate`);
    } catch (err) {
      console.warn('GEO finalize regenerate failed:', err);
      return;
    }
    setTimeout(() => {
      // Dialog closed or moved to a different entry since the click — the
      // check is stale, skip it rather than toast about the wrong entry.
      if (dialogCreatorIdRef.current !== entryId) return;
      const after = geoFinalizeRef.current;
      const neverStarted =
        after?.status !== 'pending' &&
        after?.status !== 'processing' &&
        after?.status === before?.status &&
        after?.analysis_id === before?.analysis_id;
      if (neverStarted) {
        // Deliberately non-committal — the heuristic can't tell the cause apart
        // (and a slow-but-genuine enqueue could be a false positive).
        toast.warning(
          "Regenerate may not have started — this can happen if the script's GEO pack hasn't been generated yet, content review isn't complete, or a finalize is already running. Check Script Review, or wait a moment and try again.",
        );
      }
    }, GEO_REGENERATE_NOOP_CHECK_MS);
  };

  // Rephrase a single finalized element: regenerate it on the pack first, then
  // re-finalize to pull the fresh copy through. Sequential — the second call
  // only fires once the first changed the pack.
  const handleRephraseGeoElement = async (element: 'chapters' | 'description') => {
    if (!dialogCreatorId) return;
    const entryId = dialogCreatorId;
    try {
      await apiClient.post(`/roster/${encodeURIComponent(entryId)}/geo/regenerate`, { element });
    } catch (err) {
      console.warn('GEO element regenerate failed:', err);
      return;
    }
    try {
      await apiClient.post(`/roster/${encodeURIComponent(entryId)}/geo/finalize/regenerate`);
    } catch (err) {
      console.warn('GEO finalize re-run (after rephrase) failed:', err);
    }
  };

  // Per-element finalize selection: GeoFinalizeTab owns the selection set and
  // hands over the full next array on every toggle, so this is a thin POST with
  // no local state of its own to keep in sync.
  const handleSaveGeoSelection = async (selected: string[]) => {
    if (!dialogCreatorId) return;
    try {
      await apiClient.post(`/roster/${encodeURIComponent(dialogCreatorId)}/geo/finalize/selection`, {
        selected,
      });
    } catch (err) {
      console.warn('GEO finalize selection save failed:', err);
    }
  };

  // URL deep-link discriminator. Lifted above the cascade so the cascade can
  // defer when a content-review deep link is forcing the 'review' bucket.
  const { panel: searchPanel, creator: searchCreator } = useSearch({
    strict: false,
  }) as Partial<CampaignSearch>;
  const isContentDeepLink = searchPanel === 'content-review' && !!searchCreator;

  // One-time tab decision once all three pills settle: role-default first,
  // fall back if that bucket is empty. Always sets a real value so the
  // component leaves skeleton. Defers to a content-review deep link when present.
  useEffect(() => {
    // Public mode pins the bucket directly and this cascade never fires there;
    // guarded explicitly so it can't write stray state.
    if (isPublicView) return;
    if (cascadeApplied.current) return;
    if (isInitialLoading) return;
    if (isContentDeepLink) return;
    cascadeApplied.current = true;
    if (isAdminViewer) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot role-default cascade (guarded by cascadeApplied ref, deferred until pills settle and no deep link is active); fires the tab out of skeleton exactly once, not a prop→state sync.
      setActiveTab(pendingCount > 0 ? 'pending' : reviewCount > 0 ? 'review' : 'pending');
    } else {
      setActiveTab(reviewCount > 0 ? 'review' : pendingCount > 0 ? 'pending' : 'review');
    }
  }, [isInitialLoading, pendingCount, reviewCount, isAdminViewer, isContentDeepLink, isPublicView]);

  // "Submit content on behalf" target: the first submittable row on the current
  // (server-paginated) page — page-scoping is intended, not a regression.
  const addContentTarget = useMemo(
    () =>
      permissions?.canSubmitContentOnBehalf
        ? activeRows.find((c) => canSubmitContentRow(c))
        : undefined,
    [activeRows, permissions?.canSubmitContentOnBehalf],
  );

  // Cache the open dialog's row on sight. Under server-side pagination the row
  // can leave `activeRows` (paging, a recency refetch) while its dialog is open,
  // so re-deriving would strand the panel.
  const [cachedDialogRow, setCachedDialogRow] = useState<RosterEntryContent | null>(null);
  const rowOnPage = dialogCreatorId
    ? activeRows.find((x) => x.id === dialogCreatorId) ?? null
    : null;
  // Adjust the cache while rendering (guarded against looping): refresh when the
  // row is on-page, clear when the dialog closes. `dc` prefers the fresh row.
  if (rowOnPage && rowOnPage !== cachedDialogRow) {
    setCachedDialogRow(rowOnPage);
  } else if (!dialogCreatorId && cachedDialogRow !== null) {
    setCachedDialogRow(null);
  }
  const dc = dialogCreatorId ? rowOnPage ?? cachedDialogRow : null;
  // Single source of truth for this thread's lock: the composer gate and the
  // Edit/Delete gate must never be able to drift apart.
  const contentThreadLocked = resolveThreadLocked(
    contentThreadQuery.data?.locked,
    dc?.content_thread_locked ?? false,
    threadLockedLive,
  );

  // Close the review panel on pill change only — not on "row missing from
  // activeRows", which under pagination would slam it shut on every page change.
  const prevPillForDialogRef = useRef(effectiveActiveTab);
  useEffect(() => {
    if (prevPillForDialogRef.current === effectiveActiveTab) return;
    prevPillForDialogRef.current = effectiveActiveTab;
    if (dialogCreatorId && !justApprovedIds.has(dialogCreatorId)) closeReview();
  }, [effectiveActiveTab, dialogCreatorId, closeReview, justApprovedIds]);

  // ── Deep link: ?panel=content-review&creator=<username>&thread=open ─────────
  // One-way (URL -> state). Review-able rows live in the 'review' bucket, so a
  // deep link must (a) switch the bucket to 'review' so the target loads, then
  // (b) resolve the username against those rows and open the dialog.
  const { creatorId: deepLinkCreatorId, threadOpen: deepLinkThreadOpen } =
    useCampaignReviewDeepLink(activeRows, 'content-review');

  // (a) Force the 'review' bucket once per deep-link target so the row loads.
  const deepLinkBucketKey = `${campaignId ?? ''}|${isContentDeepLink ? searchCreator : ''}`;
  const deepLinkBucketApplied = useRef('');
  useEffect(() => {
    // Public mode never resolves a deep link; opening a dialog here would fire
    // a real network request.
    if (isPublicView) return;
    if (!isContentDeepLink) return;
    if (deepLinkBucketApplied.current === deepLinkBucketKey) return;
    // eslint-disable-next-line react-hooks/immutability -- one-shot guard ref for the deep-link bucket force; deliberately mutated here (and reset in the campaign-switch effect above) to make the StrictMode double-invoke idempotent.
    deepLinkBucketApplied.current = deepLinkBucketKey;
    cascadeApplied.current = true;
    setActiveTab('review');
  }, [isContentDeepLink, deepLinkBucketKey, campaignId, isPublicView]);

  // (b) Once the target resolves against the loaded 'review' entries, open it.
  useEffect(() => {
    if (isPublicView) return;
    if (!deepLinkCreatorId) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- opens the dialog once the deep-link target resolves against the loaded review rows; functional updater is a no-op when already open, part of the ref-coupled deep-link machine, not a prop→state sync.
    setDialogCreatorId((id) => (id === deepLinkCreatorId ? id : deepLinkCreatorId));
  }, [deepLinkCreatorId, isPublicView]);

  // ── Deep link: ?tab=content&pill=<bucket> ───────────────────────────────────
  // One-way (URL -> state). Restores the list bucket from a plain tab share link
  // (returns null under a content-review deep link, so review-forcing wins).
  // Ref-guarded so it doesn't fight manual pill clicks.
  const deepLinkBucket = useCampaignBucketDeepLink('content');
  const deepLinkBucketAppliedKey = useRef('');
  useEffect(() => {
    if (isPublicView) return;
    if (!deepLinkBucket) return;
    const key = `${campaignId ?? ''}|${deepLinkBucket}`;
    if (deepLinkBucketAppliedKey.current === key) return;
    // eslint-disable-next-line react-hooks/immutability -- one-shot guard ref for the bucket deep-link restore; deliberately mutated here (and reset in the campaign-switch effect above) to make the StrictMode double-invoke idempotent.
    deepLinkBucketAppliedKey.current = key;
    cascadeApplied.current = true;
    setActiveTab(deepLinkBucket);
  }, [deepLinkBucket, campaignId, isPublicView]);

  // Parked: staged for the "Add content" entry point (not yet wired to a button).
  const _handleAddContent = () => {
    if (!addContentTarget) return;
    // Jump to the matching sub-tab (BE-driven status).
    const status = String(addContentTarget.status || '').toLowerCase();
    const target: ContentBucket =
      status === 'content_review' ? 'review' : 'pending';
    // Treat the programmatic switch as explicit so the cascade doesn't bounce back.
    handleTabChange(target);
    openReview(addContentTarget);
    setContentSubmitOpen(addContentTarget.id, true);
  };

  const contentBusy = dc ? (contentActionBusy ?? false) : false;
  // Parked: consumed by the embedded content-review agent UI (staged).
  const _crUiFeedback = dc ? (contentReviewFeedback[dc.id] ?? '') : '';
  const _crUiMode = dc ? (contentReviewUiMode[dc.id] ?? null) : null;
  // Brand reviews when status === 'content_review'. Approved rows leave this
  // tab (they become live_pending on the Live tab), so `isApproved` is always
  // false here — branching on canReviewThisContent suffices.
  const contentInReview = dc?.status === 'content_review';
  const isApproved = false;
  const canReviewThisContent =
    !!dc && contentInReview && !!permissions?.canReviewContent;

  const urlUpload = uploadCopy(campaignPlatform);



  // Once AI analysis completes the BE returns an S3 presigned URL in `url`.
  // While still processing, `url` is the (non-embeddable) Drive URL, so we
  // show a processing spinner instead.
  const rawContentUrl = contentDetail?.url ?? '';
  const isDriveUrl =
    rawContentUrl.includes('drive.google.com') ||
    rawContentUrl.includes('docs.google.com');
  const videoPrimary = rawContentUrl;
  const videoProcessing = isDriveUrl && !!rawContentUrl; // Drive URL = AI not done yet
  const videoSecondary: string | undefined = undefined;

  // Cost lives on the Finalized tab's row (locked_budget), not on Review rows.
  const costLabel: string | undefined = undefined;

  const _contentAgentPanel =
    dc && typeof renderEmbeddedContentReviewAgent === 'function'
      ? renderEmbeddedContentReviewAgent(dc as unknown as Record<string, unknown>, closeReview)
      : undefined;

  // Server-persisted column visibility (Columns dropdown) — holds per user
  // across devices/browsers; see useColumnPreferences.
  const columnState = useRosterColumnPreferences(campaignId, 'content');

  // While activeTab === null the shared shell renders its own loading skeleton.

  // ── Column definitions ──────────────────────────────────────────────────────
  // Every column is fixed and capped to its content's realistic ceiling;
  // DataTable's trailing spacer absorbs leftover width. Memoized (like the other
  // roster tabs) and built ABOVE the early return so the hooks run
  // unconditionally (Rules of Hooks); permissions reads are optional-chained so
  // construction is safe while permissions is still null.
  const columns = useMemo<ColumnDef<RosterEntryContent>[]>(() => {
    return [
      {
        id: 'creator',
        alwaysVisible: true,
        header: 'Creator',
        // Matches Creators/Confirmed/Scripts (320/340). PlatformCreatorCell
        // self-truncates (min-w-0 + truncate), so a long name ellipsizes instead
        // of widening the column — the old fixed 390 is no longer needed.
        minWidth: 320,
        maxWidth: 340,
        grow: true,
        sticky: 'left',
        // The one sortable column on this basic-info tab (server-side, by display
        // name). Disabled in public view, like the other roster tabs.
        sortable: !isPublicView,
        cell: (c) => (
          /* Platform name + clickable @username. See lib/platformProfile.ts. */
          <PlatformCreatorCell
            creator={c as unknown as Record<string, unknown>}
            campaignPlatform={campaignPlatform}
          />
        ),
      },
      {
        id: 'status',
        alwaysVisible: true,
        header: 'Status',
        // "Content Review" badge measures 127px + 16px cell padding = 143.
        minWidth: 145,
        maxWidth: 145,
        cell: (c) => {
          // Permanent skeleton in public mode: the public wire carries no
          // content-review status, so there's nothing real to show.
          if (isPublicView) return <Skeleton className="h-6 w-24 rounded-full" />;
          return <StatusBadge status={c.status} />;
        },
      },
      {
        id: 'lastComment',
        header: 'Last comment',
        // Floor must exceed the bubble's own maxWidth (200) plus cell padding,
        // or the column could shrink narrower than its content.
        minWidth: 216,
        maxWidth: 224,
        wrap: true,
        cell: (c) => {
          // Permanent skeleton in public mode — the public wire carries no `last_message`.
          if (isPublicView) return <Skeleton className="h-8 w-40 rounded-full" />;
          const lastMsg = c.last_message;
          if (!lastMsg?.body) return <p className="text-sm text-muted-foreground">—</p>;
          const isOurMessage = lastMsg.sender_role === viewerActor;
          const tooltip = lastMsg.sender_name ? `${lastMsg.sender_name}: ${lastMsg.body}` : lastMsg.body;
          // Sender-aware bubble; inner span clamps to 2 lines. Background is a
          // custom property (not `background`) so the hover class can win.
          const bubbleStyle = {
            display: 'inline-block',
            maxWidth: 200,
            padding: '10px 16px',
            borderRadius: 'var(--radius)',
            color: isOurMessage ? '#FFFFFF' : 'var(--muted-foreground)',
            border: isOurMessage
              ? '1px solid var(--background-primary-default)'
              : '1px solid var(--stone-200)',
            fontSize: 14,
            lineHeight: '20px',
            '--bubble-bg': isOurMessage ? 'var(--background-primary-default)' : 'var(--stone-100)',
            '--bubble-hover-bg': isOurMessage
              ? 'var(--background-primary-default-hover)'
              : 'var(--stone-200)',
          } as React.CSSProperties;
          const preview = <span className="line-clamp-2">{lastMsg.body}</span>;
          // Only content_review rows resolve the review deep link; others stay
          // an inert bubble rather than a click that opens nothing.
          const canOpenReview = c.status === 'content_review';
          if (!canOpenReview) {
            return (
              <SimpleTooltip content={tooltip}>
                <span className="bg-(--bubble-bg)" style={bubbleStyle}>
                  {preview}
                </span>
              </SimpleTooltip>
            );
          }
          return (
            <SimpleTooltip content={tooltip}>
              <button
                type="button"
                aria-label={`Open content review comments for ${rowDisplayName(c)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (dialogCreatorId === c.id) closeReview();
                  else openReview(c);
                }}
                className="bg-(--bubble-bg) hover:bg-(--bubble-hover-bg) focus-visible:ring-ring cursor-pointer text-left transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
                style={bubbleStyle}
              >
                {preview}
              </button>
            </SimpleTooltip>
          );
        },
      },
      {
        id: 'updated',
        header: 'Updated',
        // Owns the row's whole time story: when it last changed, and — via
        // EtaTimeline — when the next one (delivery or revision) is expected.
        minWidth: 150,
        maxWidth: 150,
        cell: (c) => {
          // Permanent skeleton in public mode — the public wire carries no `status_updated_at`.
          if (isPublicView) return <Skeleton className="h-4 w-24" />;
          const canEditEta =
            !!permissions?.canSubmitContentOnBehalf && typeof handleAdminSetEta === 'function';
          return (
            <EtaTimeline
              status={c.status}
              phase="content"
              eta={c.content_eta ?? null}
              updatedAt={c.content_updated_at}
              canEditEta={canEditEta}
              onSetEta={(iso) => handleAdminSetEta?.(c.id, 'content', iso)}
            />
          );
        },
      },
      {
        id: 'action',
        alwaysVisible: true,
        header: 'Action',
        // Role-aware fixed width. Admin's left slot holds Review (+ the v2/New
        // tags once a revision has landed) and the right slot the
        // "+ Add/Re-submit URL" button. Brand only ever sees Review. The ETA
        // itself lives in the Updated column (EtaTimeline), not here.
        minWidth: permissions?.canSubmitContentOnBehalf ? 260 : 140,
        maxWidth: permissions?.canSubmitContentOnBehalf ? 260 : 140,
        cell: (c) => {
          // Permanent skeleton in public mode — no interactive action is rendered
          // here (read-only permissions, and no per-row content-review status).
          if (isPublicView) return <Skeleton className="h-8 w-16 rounded-full" />;
          const hasUrl = !!c.live_url;
          const hasAddUrl = permissions?.canSubmitContentOnBehalf && typeof handleAdminSubmitContent === 'function';
          return (
            // Two fixed slots (justify-between) so a lone "Add/Re-submit URL"
            // pins to the right edge instead of centering.
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center">
                <ReviewAction
                  status={c.status}
                  phase="content"
                  version={c.version}
                  unreadCount={c.unread_count}
                  hasEta={!!c.content_eta}
                  seen={c.content_seen}
                  onToggleReview={() => (dialogCreatorId === c.id ? closeReview() : openReview(c))}
                />
              </div>
              <div className="flex items-center">
                {hasAddUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    aria-label={hasUrl ? 'Re-submit content URL' : 'Add content URL'}
                    onClick={() => setAddContentRow({ id: c.id, existingUrl: c.live_url ?? '', name: rowDisplayName(c), expectedContentKind: c.expected_content_kind })}
                    className="h-8 gap-1 rounded-full px-3 text-caption-medium hover:bg-(--brand-50) hover:text-(--brand-500)"
                    style={{ borderColor: '#e5e5e5' }}
                  >
                    <Plus size={14} weight="bold" /> {submitLabel(hasUrl)}
                  </Button>
                )}
              </div>
            </div>
          );
        },
      },
    ];
    // Action column stays visible on every sub-tab; its cell logic gates Review
    // and Add/Re-submit URL per row, falling back to "—" when neither applies.
  }, [
    isPublicView,
    permissions,
    viewerActor,
    campaignPlatform,
    handleAdminSetEta,
    handleAdminSubmitContent,
    dialogCreatorId,
    closeReview,
    openReview,
    setAddContentRow,
  ]);

  const columnView = useMemo(
    () => columnVisibilityView(columns, columnState.hiddenIds),
    [columns, columnState.hiddenIds],
  );

  // All hooks declared above — early returns come last (Rules of Hooks).
  if (!permissions) return null;

  // Empty state for DataTable — global (nothing at this stage) vs per-filter.
  const emptyState =
    !isLoadingEntries && allCount === 0 ? (
      <EmptyState
        icon={VideoCamera}
        title="No scripts approved yet"
        body="Creators appear here once their script is approved."
      />
    ) : effectiveActiveTab === 'review' ? (
      <EmptyState
        icon={VideoCamera}
        title="No content to review"
        body="Content creators submit for your approval show here."
      />
    ) : effectiveActiveTab === 'pending' ? (
      <EmptyState
        icon={VideoCameraSlash}
        title="No content pending"
        body="Creators which are yet to share their content."
      />
    ) : (
      <EmptyState
        icon={MonitorPlay}
        title="No content yet"
        body={
          scriptStageOn
            ? 'Filming starts once scripts are approved. Creators upload their videos, our Content Review agent checks each against its script and brand safety, then it reaches you for the final call.'
            : 'Creators upload their videos, our Content Review agent checks each against brand safety, then it reaches you for the final call.'
        }
        action={
          scriptStageOn
            ? {
                label: 'View scripts',
                onClick: () =>
                  navigate({
                    to: '.',
                    search: ((p: Partial<CampaignSearch>) => ({ ...p, tab: 'scripts', pill: undefined })) as never,
                  }),
              }
            : undefined
        }
      />
    );

  // Public-mode pagination display from the grant snapshot (the hook's own
  // values are disabled/wrong). The `total_pages > 1` condition drives both
  // hideSummary and the granted pill's badge — they must never disagree.
  const publicPageSize = grant?.pageSize ?? pageSize;
  const publicGrantedRowCount = rosterPage?.rows.length ?? 0;
  const displayPage = isPublicView ? (rosterPage?.page ?? grant?.page ?? 1) : page;
  const displayTotalPages = isPublicView
    ? (rosterPage?.total_pages ?? grant?.totalPages ?? 1)
    : pageCount;
  const hideSummary = isPublicView && displayTotalPages > 1;
  const displayTotal = isPublicView
    ? displayTotalPages > 1
      ? Math.max(publicGrantedRowCount, 1)
      : publicGrantedRowCount
    : total;

  return (
    <>
      <JumpToTopPill isRefetching={isLoadingEntries} page={page} onJump={() => setPage(1)} />
      <div className="flex min-h-0 flex-1 flex-col gap-5">
        <div className="flex shrink-0 items-start justify-between gap-4">
          <RosterScreenHeader
            title="Content"
            subtitle="One last check before going live."
          />
        </div>

        <RosterTableShell<ContentBucket>
          fill
          activeTab={effectiveActiveTab}
          // Public mode prompts sign-in (click captured for resume) instead of
          // switching pills directly — see handlePublicPillClick.
          onTabChange={isPublicView ? handlePublicPillClick : handleTabChange}
          // Search has no meaning against a single fetched page in public mode,
          // so it's hidden entirely rather than wired to a no-op.
          controls={
            isPublicView ? undefined : (
              <RosterSearchSort
                query={searchState.query}
                onQueryChange={searchState.setQuery}
                columnsMenu={
                  <ColumnsMenu
                    items={columnView.menuItems}
                    hiddenCount={columnView.hiddenCount}
                    onToggle={columnState.toggle}
                    onReset={columnState.reset}
                  />
                }
              />
            )
          }
          // Public mode never shows the whole-tab skeleton — displayPills
          // already carries a per-pill 'loading' sentinel for every pill
          // except the granted one.
          tabsLoading={isPublicView ? false : activeTab === null}
          tabs={displayPills}
          page={displayPage}
          totalPages={displayTotalPages}
          total={displayTotal}
          // Never a fabricated campaign-wide total — see displayTotal above.
          hideSummary={hideSummary}
          onPage={isPublicView ? PUBLIC_NOOP : setPage}
          pageSize={isPublicView ? publicPageSize : pageSize}
          pageSizeOptions={isPublicView ? undefined : PAGE_SIZE_OPTIONS}
          onPageSizeChange={isPublicView ? undefined : setPageSize}
        >
          {isContentQueryError ? (
            <p className="py-16 text-center text-sm text-rose-500">
              Couldn't load content. Please refresh.
            </p>
          ) : (
            <DataTable<RosterEntryContent>
              fillHeight
              columns={columnView.visibleColumns}
              data={displayedRows}
              getRowId={(c) => c.id}
              sort={rosterSort.sortProp(effectiveActiveTab, { disabled: isPublicView, onChange: () => setPage(1) })}
              // isInitialLoading would never settle in public mode, but the
              // granted rows are already available, so render them immediately.
              loading={isPublicView ? false : isInitialLoading || activeTab === null}
              emptyState={emptyState}
              className="border-0 rounded-none bg-transparent"
            />
          )}
        </RosterTableShell>
      </div>

      {dc ? (
        <ContentReviewPanel
          // Remount per entry so GEO finalize state never leaks across a switch.
          key={dc.id}
          open
          onClose={closeReview}
          onThreadOpenChange={setThreadOpenUrl}
          title="AI Content Review"
          // entryId only feeds the GEO action mutations (showGeo gates
          // visibility). GEO finalize is prop-fed via geoAnalysis; no fetch here.
          entryId={dc.id}
          geoAnalysis={contentDetail?.geo_finalize}
          showGeo={(campaignPlatform ?? '').toLowerCase().includes('youtube')}
          // GEO writes are admin-only: brand-only viewers get `undefined` for
          // all three, so ContentResultTabs hides every GEO write affordance.
          onRegenerateGeoFinalize={isAdminViewer ? handleRegenerateGeoFinalize : undefined}
          onRephraseGeoElement={isAdminViewer ? handleRephraseGeoElement : undefined}
          onSaveGeoSelection={isAdminViewer ? handleSaveGeoSelection : undefined}
          creatorName={rowDisplayName(dc)}
          creatorAvatarUrl={(dc.platform?.profile_picture_url) || undefined}
          creatorHandle={(dc.platform?.username) || undefined}
          assignedProducts={contentDetail?.assigned_products}
          campaignProductCount={campaignHeader?.products?.length ?? 0}
          shareCreatorUsername={(dc.platform?.username) || undefined}
          statusLabel={contentStatusLabel(dc.status)}
          updatedLabel={fmtDateTime(dc.status_updated_at)}
          costLabel={costLabel}
          audience={permissions?.canSubmitContentOnBehalf ? 'admin' : 'brand'}
          aiAnalysis={contentDetailLoading && !contentDetail ? undefined : contentDetail?.ai_analysis}
          thread={contentThread}
          unreadCount={(dc as { unread_count?: number }).unread_count ?? 0}
          onThreadOpen={() => {
            if (dialogCreatorId) {
              markThreadRead.mutate({ entryId: dialogCreatorId, type: 'content' });
            }
          }}
          canAddComment={!contentThreadLocked && typeof handleContentThreadComment === 'function'}
          // Explicit lock signal. canAddComment above also requires a post
          // handler, so it is NOT a pure lock state here — without this, a
          // surface with no comment handler would lose Edit/Delete on an
          // otherwise-open thread.
          locked={contentThreadLocked}
          addCommentBusy={postMessage.isPending}
          // Return the promise (not void-wrapped) so ChatThread's submit()
          // actually awaits it and can roll back the optimistic bubble +
          // restore the draft on a failed send (see handleContentThreadComment).
          onAddComment={(body, replyTo) =>
            handleContentThreadComment ? handleContentThreadComment(dc.id, body, replyTo) : undefined
          }
          onEditMessage={handleContentThreadEdit}
          onDeleteMessage={handleContentThreadDelete}
          viewerIsSuperAdmin={viewerIsSuperAdmin}
          viewerActor={viewerActor}
          videoUrl={videoPrimary}
          videoProcessing={videoProcessing}
          videoSecondaryUrl={videoSecondary}
          leftExtras={<ContentRowDetail c={dc} layout="embed" hideReviewActions />}
          canReview={canReviewThisContent}
          reviewBusy={contentBusy}
          isApproved={justApprovedIds.has(dc.id)}
          onApprove={() => void handleApproveContent(dc.id)}
          showApprovedBanner={isApproved}
          approvedBannerText="Content approved"
          defaultThreadOpen={deepLinkThreadOpen}
        />
      ) : null}

      {/* Add Content URL modal — shown when an admin clicks a row's + button.
          Pre-fills the existing URL on re-submit so the admin can edit it. */}
      <UrlInputModal
        open={!!addContentRow}
        onClose={() => setAddContentRow(null)}
        onSave={async (url) => {
          if (!addContentRow || typeof handleAdminSubmitContent !== 'function') return;
          await handleAdminSubmitContent(addContentRow.id, { url });
        }}
        title={addContentRow?.existingUrl ? 'Re-submit content URL' : 'Add content URL'}
        description={urlUpload.description}
        initialUrl={addContentRow?.existingUrl ?? ''}
        placeholder={urlUpload.placeholder}
        saveLabel={addContentRow?.existingUrl ? 'Replace URL' : 'Save URL'}
        requirePlatform={urlUpload.requirePlatform}
        expectedContentKind={addContentRow?.expectedContentKind}
      />
    </>
  );
}
